import React, { Component, Children, cloneElement } from 'react';
import classnames from 'classnames';
import lodash from 'lodash';

import { Button, BUTTON } from '../Button/Button';
const { BGCOLOR, COLOR } = BUTTON;
import { Conditional } from 'common/components/layout';
import styles from './Workflow.scss';

import {
    TwoColumnScaffold,
    LeftColumn,
    RightColumn,
}from 'pages/studyOverview/components/TwoColumnScaffold';

/**
 * @class Workflow
 * @param {Object}   props
 * @param {String}   props.title The Workflow Title
 * @param {String}   props.subTitle The Workflow Sub Title
 * @param {String}   props.cancelButtonText The cancel button label text
 * @param {Function} props.onCancel A hook for workflow on cancel event
 * @param {Function} [props.initializeWorkflow] A hook for initializing workflow data
 * @param {Function} [props.onCompleteWorkflow] A hook for workflow onComplete event
 * @param {Object}   [props.sharedStepData] Initial step data shared across the workflow
 * 
 * @example
 * <Workflow 
 *   title={'Example Workflow'} 
 *   cancelButtonText={'Cancel Workflow'} 
 *   onCancel={onCancelHandler}>
 * 
 *   <Step title={'Step 1'} StepComponent={Component1} />
 *   <Step title={'Step 2'} StepComponent={Component2} />
 * 
 * </Workflow>
 * 
 */
export default class Workflow extends Component {
    constructor(props) {
        super(props);

        this.state = {
            initialStepIndex: 0,
            currentStepIndex: 0,
            steps: this.initializeStepData(props.children)
        };

        this.progressToNextStep = this.progressToNextStep.bind(this);
        this.navigateToStep = this.navigateToStep.bind(this);
        this.initializeWorkflow();
    }

    initializeWorkflow() {
        const { initializeWorkflow } = this.props;
        if(initializeWorkflow) {
            initializeWorkflow();
        }
    }

    completeWorkflow() {
        const { onCompleteWorkflow } = this.props;
        if(onCompleteWorkflow) {
            onCompleteWorkflow();
        }
    }

    initializeStepData(steps) {
        return steps.map(step => { 
            return {...step.props, isValid: false, isComplete: false}; 
        });
    }

    isLastStep() {
        const { currentStepIndex, steps } = this.state;
        return currentStepIndex === steps.length - 1;
    }

    updateCurrentStepState(isValid=false, isComplete=false) {
        const { currentStepIndex, steps } = this.state;
        const updatedSteps = [...steps];

        const currentStep = updatedSteps[currentStepIndex];
        currentStep.isValid = isValid;
        currentStep.isComplete = isComplete;

        updatedSteps[currentStepIndex] = currentStep;
        return updatedSteps;
    }

    setStepValidation(isValid) {
        const updatedSteps = this.updateCurrentStepState(isValid);

        this.setState({
            steps: updatedSteps
        });
    }

    progressToNextStep() {
        const { currentStepIndex } = this.state;
        const isLastStep = this.isLastStep();

        const updatedSteps = this.completeCurrentStep();
        const stepIndexIncrement = isLastStep ? 0 : 1; 

        this.setState({
            currentStepIndex: currentStepIndex + stepIndexIncrement,
            steps: updatedSteps
        });

        if (this.props.onNextStep) {
            this.props.onNextStep();
        }

        if (isLastStep) {
            return this.completeWorkflow();
        }
    }

    completeCurrentStep() {
        const isValid = true, isComplete = true;
        return this.updateCurrentStepState(isValid, isComplete);
    }

    navigateToStep(stepIndex) {
        const { steps } = this.state;
        const stepToNavigateTo = steps[stepIndex];

        if(stepToNavigateTo.isComplete) {
            this.setState({
                currentStepIndex: stepIndex,
            });
        }
    }

    getCurrentStepComponent() {
        const { currentStepIndex } = this.state;
        const stepProps = {
            progressToNextStep: this.progressToNextStep,
            sharedStepData: this.props.sharedStepData
        };

        return Children.map(
            this.props.children,
            (step, index) => index === currentStepIndex
                ? cloneElement(step, stepProps)
                : null
        );
    }

    componentDidUpdate(prevProps) {
        if (!lodash.isEqual(this.props.sharedStepData, prevProps.sharedStepData)) {
            this.getCurrentStepComponent();
        }
    }

    render() {
        const {
            title,
            subTitle,
            onCancel,
            cancelButtonText,
            showCancelButton,
        } = this.props;

        return (
            <TwoColumnScaffold className={styles.scaffold} data-cy="Workflow">
                <LeftColumn className={styles.leftColumn}>
                    <Sidebar
                        title={title}
                        subTitle={subTitle}
                        steps={this.state.steps}
                        currentStepIndex={this.state.currentStepIndex}
                        navigateToStep={this.navigateToStep}
                        cancelButtonText={cancelButtonText || 'Cancel'}
                        showCancelButton={showCancelButton}
                        onCancel={onCancel}
                        data-cy="Workflow-onCancel"
                    />
                </LeftColumn>
                <RightColumn className={styles.rightColumn}>
                    {this.getCurrentStepComponent()}
                </RightColumn>
            </TwoColumnScaffold>
        );
    }
}

export const Sidebar = (props) => {
    const {
        title,
        subTitle,
        steps,
        currentStepIndex,
        navigateToStep,
        cancelButtonText,
        showCancelButton,
        onCancel,
    } = props;
    return (
        <div className={classnames(styles.sidebar, {
            [styles.sidebarWithoutBtn]: !showCancelButton,
        })}  data-cy="Workflow-Sidebar">
            <SidebarHeader title={title} subTitle={subTitle} />
            <SidebarSteps
                className={styles.sidebarNav}
                steps={steps}
                currentStepIndex={currentStepIndex}
                navigateToStep={navigateToStep}
            />
            <Conditional condition={showCancelButton}>
                <Button
                    color={COLOR.WHITE}
                    bgcolor={BGCOLOR.RED}
                    onClick={() => onCancel()}
                    data-cy="Workflow-onCancel"
                >
                    { cancelButtonText }
                </Button>
            </Conditional>
        </div>
    );
};

export const SidebarHeader = ({ title, subTitle }) => (
    <div data-cy="Workflow-SidebarHeader">
        <h1 data-cy="Workflow-title">{title}</h1>
        <h2 data-cy="Workflow-subTitle">{subTitle}</h2>
    </div>
);

export const SidebarSteps = (props) => (
    <nav className={props.className} data-cy="Workflow-SidebarSteps">
        <ul data-cy="Workflow-ul">
            {
                props.steps
                    .map((step, index) => 
                        <SidebarStep
                            key={index}
                            step={step}
                            isCurrentStep={index === (props.currentStepIndex || 0)}
                            onClick={() => props.navigateToStep(index)}
                            data-cy={'Workflow-li' + index}
                        />
                    )
            }
        </ul>
    </nav>
);

export const SidebarStep = ({ step, isCurrentStep, onClick }) => (
    <li
        className={
            classnames({
                [styles.active]: isCurrentStep,
                [styles.completed]: step.isComplete
            })
        }
        onClick={onClick}
        data-cy="Workflow-SidebarStep"
    >
        <h3 data-cy="Workflow-title">{step.title}</h3>
        <StepIcon isComplete={step.isComplete} />
    </li>
);

export const StepIcon = ({ isComplete }) => (
    <i className={styles.stepIcon} data-cy="Workflow-StepIcon">
        {isComplete ? 'check_circle' : 'radio_button_unchecked'}
    </i>
);


/**
 * A Workflow is comprised of Step components that encapsulate the 
 * complexity of each step while providing a consistent interface.
 * @param {Object} [props] 
 * @param {JSX.Element | React.Component} props.StepComponent A component that will implement Step
 * @param {String}   props.title The Step title
 * @param {Function} [props.onFieldChange] An event handler for onChange events within a step
 * @param {Function} [props.onFieldBlur] An event handler for onBlur events within a step
 * @param {Function} props.progressToNextStep A hook to enable progessing to the next step (Workflow Prop)
 * @param {Object}   [props.sharedStepData] Initial step data shared across the workflow (Workflow Prop)
 */
export const Step = ({
    StepComponent, 
    title, 
    onFieldChange, 
    onFieldBlur, 
    progressToNextStep, 
    sharedStepData, 
    ...props
}) => {
    return (
        <div className={styles.stepContent} data-cy="Workflow-Step">
            <StepComponent
                title={title}
                onFieldChange={onFieldChange}
                onFieldBlur={onFieldBlur}
                stepData={sharedStepData}
                progressToNextStep={progressToNextStep}
                {...props} />
        </div>
    );
};
